import { aeo_faq_json_ld, AEO_SITE_URL } from './aeo-discovery'
import { PRODUCT } from './platform'

const SITE_URL = AEO_SITE_URL
const BRAND = PRODUCT.brand
const BRAND_FULL = PRODUCT.brand_full
const DEFAULT_OG_IMAGE = '/assets/og-default.png'
const INDEXABLE_ROBOTS = 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'
const PRIVATE_ROBOTS = 'noindex, nofollow'

export interface SeoConfig {
  title: string
  description: string
  path: string
  no_index?: boolean
  og_image?: string
}

const ROUTE_SEO: Record<string, SeoConfig> = {
  '/': {
    title: BRAND_FULL,
    description: PRODUCT.seo.home_description,
    path: '/',
  },
  '/login': {
    title: `Entrar — ${BRAND}`,
    description: PRODUCT.seo.login_description,
    path: '/login',
    no_index: true,
  },
  '/privacy': {
    title: `LGPD — ${BRAND}`,
    description: PRODUCT.seo.privacy_description,
    path: '/privacy',
    no_index: true,
  },
  '/onboarding': {
    title: `Primeiros passos — ${BRAND}`,
    description: PRODUCT.seo.onboarding_description,
    path: '/onboarding',
    no_index: true,
  },
  '/conformidade': {
    title: `Painel de conformidade — ${BRAND}`,
    description: 'Biblioteca de requisitos e percentual de conformidade por norma.',
    path: '/conformidade',
    no_index: true,
  },
  '/gap-analysis': {
    title: `Gap analysis — ${BRAND}`,
    description: 'Questionário Sim/Não/Parcial por requisito com % e rascunho de CAPA.',
    path: '/gap-analysis',
    no_index: true,
  },
  '/auditorias': {
    title: `Auditorias — ${BRAND}`,
    description: 'Planejamento e execução de auditorias internas, externas e de fornecedor.',
    path: '/auditorias',
    no_index: true,
  },
  '/nao-conformidades': {
    title: `Não conformidades — ${BRAND}`,
    description: 'Registro, severidade e workflow de tratamento de não conformidades.',
    path: '/nao-conformidades',
    no_index: true,
  },
}

const PRIVATE_PREFIXES = [
  '/modules',
  '/admin',
  '/account',
  '/security',
  '/onboarding',
  '/conformidade',
  '/gap-analysis',
  '/auditorias',
  '/nao-conformidades'
]

function absolute_url(path_value: string): string {
  const clean = path_value.startsWith('/') ? path_value : `/${path_value}`
  return new URL(clean, SITE_URL).toString()
}

export function seo_for_path(pathname: string): SeoConfig {
  const clean = pathname.split('?')[0].split('#')[0] || '/'
  const known = ROUTE_SEO[clean]
  if (known) return known

  if (PRIVATE_PREFIXES.some((prefix) => clean.startsWith(prefix))) {
    return {
      title: BRAND_FULL,
      description: PRODUCT.seo.private_description,
      path: clean,
      no_index: true,
    }
  }

  return {
    title: `Página não encontrada — ${BRAND}`,
    description: `A rota solicitada não existe no ${BRAND}.`,
    path: clean,
    no_index: true,
  }
}

function upsert_meta(selector: string, attrs: Record<string, string>): void {
  let el = document.head.querySelector<HTMLMetaElement>(selector)
  if (!el) {
    el = document.createElement('meta')
    document.head.appendChild(el)
  }
  for (const [key, value] of Object.entries(attrs)) {
    el.setAttribute(key, value)
  }
}

function upsert_link(rel: string, attrs: Record<string, string>): void {
  const type = attrs.type || ''
  const title = attrs.title || ''
  const href = attrs.href || ''
  const selector = `link[rel="${rel}"]${type ? `[type="${type}"]` : ''}${title ? `[title="${title}"]` : ''}`
  let el = document.head.querySelector<HTMLLinkElement>(selector)
  if (!el) {
    el = document.createElement('link')
    el.setAttribute('rel', rel)
    document.head.appendChild(el)
  }
  for (const [key, value] of Object.entries(attrs)) {
    el.setAttribute(key, value)
  }
}

function upsert_json_ld(id: string, payload: Record<string, unknown>): void {
  const selector = `script[type="application/ld+json"]#${id}`
  let el = document.head.querySelector<HTMLScriptElement>(selector)
  if (!el) {
    el = document.createElement('script')
    el.type = 'application/ld+json'
    el.id = id
    document.head.appendChild(el)
  }
  el.textContent = JSON.stringify(payload)
}

export function apply_seo(config: SeoConfig): void {
  const canonical = absolute_url(config.path)
  const og_image = absolute_url(config.og_image || DEFAULT_OG_IMAGE)
  // Produto sem site público neste host (SGQ: app em adm.*) é sempre noindex.
  const robots = !PRODUCT.indexable || config.no_index ? PRIVATE_ROBOTS : INDEXABLE_ROBOTS

  document.title = config.title

  upsert_meta('meta[name="description"]', { name: 'description', content: config.description })
  upsert_meta('meta[name="robots"]', { name: 'robots', content: robots })
  upsert_meta('meta[property="og:type"]', { property: 'og:type', content: 'website' })
  upsert_meta('meta[property="og:title"]', { property: 'og:title', content: config.title })
  upsert_meta('meta[property="og:description"]', { property: 'og:description', content: config.description })
  upsert_meta('meta[property="og:url"]', { property: 'og:url', content: canonical })
  upsert_meta('meta[property="og:locale"]', { property: 'og:locale', content: 'pt_BR' })
  upsert_meta('meta[property="og:site_name"]', { property: 'og:site_name', content: BRAND_FULL })
  upsert_meta('meta[property="og:image"]', { property: 'og:image', content: og_image })
  upsert_meta('meta[property="og:image:secure_url"]', { property: 'og:image:secure_url', content: og_image })
  upsert_meta('meta[property="og:image:type"]', { property: 'og:image:type', content: 'image/png' })
  upsert_meta('meta[property="og:image:width"]', { property: 'og:image:width', content: '1200' })
  upsert_meta('meta[property="og:image:height"]', { property: 'og:image:height', content: '630' })
  upsert_meta('meta[property="og:image:alt"]', { property: 'og:image:alt', content: config.title })
  upsert_meta('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' })
  upsert_meta('meta[name="twitter:title"]', { name: 'twitter:title', content: config.title })
  upsert_meta('meta[name="twitter:description"]', { name: 'twitter:description', content: config.description })
  upsert_meta('meta[name="twitter:image"]', { name: 'twitter:image', content: og_image })
  upsert_meta('meta[name="twitter:image:alt"]', { name: 'twitter:image:alt', content: config.title })

  upsert_link('canonical', { rel: 'canonical', href: canonical })
  upsert_link('alternate', {
    rel: 'alternate',
    type: 'text/markdown',
    title: 'AI-readable site overview',
    href: '/llms.txt',
  })
  upsert_link('sitemap', {
    rel: 'sitemap',
    type: 'application/xml',
    title: 'Sitemap',
    href: '/sitemap.xml',
  })

  // JSON-LD/breadcrumb são ativos públicos do vertical SST (nr01.tec.br);
  // no SGQ o host de app é noindex e não emite dados estruturados.
  if (PRODUCT.id !== 'sst') return

  upsert_json_ld('nr01-organization', {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${SITE_URL}/#organization`,
    name: 'Exponencial Administração & Tecnologia',
    url: 'https://exponencialadm.net/',
    logo: absolute_url('/icon-512.png'),
    knowsAbout: [
      'Gestão de Riscos Ocupacionais',
      'NR-01',
      'GRO',
      'PGR',
      'Saúde e Segurança do Trabalho',
      'LGPD',
      'Auditoria de SST',
      'Answer Engine Optimization (AEO)',
      'Visibilidade em IAs',
      'Agent-ready systems',
    ],
    sameAs: [
      'https://www.instagram.com/exponencial.adm/',
      'https://br.linkedin.com/in/ronald-caselli-a5916644',
      'https://github.com/exponencialadm',
      'https://exponencialadm.net/pt-br/produtos/sigssst-nr01/',
    ],
  })

  upsert_json_ld('nr01-software', {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    '@id': `${SITE_URL}/#software`,
    name: 'NR-01',
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web',
    url: SITE_URL,
    description: config.description,
    image: og_image,
    inLanguage: 'pt-BR',
    publisher: { '@id': `${SITE_URL}/#organization` },
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'BRL',
      description: 'Trial self-serve com Google',
    },
  })

  upsert_json_ld('nr01-website', {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${SITE_URL}/#website`,
    url: SITE_URL,
    name: BRAND_FULL,
    inLanguage: 'pt-BR',
    publisher: { '@id': `${SITE_URL}/#organization` },
  })

  upsert_json_ld('nr01-webpage', {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${canonical}#webpage`,
    url: canonical,
    name: config.title,
    description: config.description,
    inLanguage: 'pt-BR',
    isPartOf: { '@id': `${SITE_URL}/#website` },
    about: { '@id': `${SITE_URL}/#software` },
    dateModified: new Date().toISOString().slice(0, 10),
  })

  if (config.path === '/') {
    upsert_json_ld('nr01-faq', aeo_faq_json_ld())
  } else {
    const faq_el = document.head.querySelector('#nr01-faq')
    if (faq_el) faq_el.remove()
  }

  if (config.path === '/' || config.path === '/login' || config.path === '/privacy') {
    const crumbs = [{ name: BRAND, path: '/' }]
    if (config.path !== '/') {
      crumbs.push({ name: config.title.replace(` — ${BRAND}`, '').replace(' — SIGSSST', ''), path: config.path })
    }
    upsert_json_ld('nr01-breadcrumb', {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: crumbs.map((crumb, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: crumb.name,
        item: absolute_url(crumb.path),
      })),
    })
  } else {
    const breadcrumb_el = document.head.querySelector('#nr01-breadcrumb')
    if (breadcrumb_el) breadcrumb_el.remove()
  }
}